import React from 'react';
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router';
import isUuid from 'is-uuid';
import { UserConst } from '@axiom/const';
import {
  ErrorLayout,
  Footer,
  HeaderBar,
  Loader,
  Page,
  Suspense,
} from '@axiom/ui';

import { AuthRoute } from './routes/AuthRoute';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { TalentProfilePage } from './pages/TalentProfilePage';
import { DigitalSubmissionsPage } from './pages/DigitalSubmissionsPage';
import { WorkFeedPage } from './pages/WorkFeedPage';
import { SingleWorkFeedItemPage } from './pages/SingleWorkFeedItemPage';
import { AvailabilityPreferencesConfirmPage } from './pages/AvailabilityPreferencesConfirmPage';
import { WorkFeedFailWhalePage } from './pages/WorkFeedFailWhalePage';
import { WorkFeedActionNeededPage } from './pages/WorkFeedActionNeededPage';
import { CommunicationsPreferencesPage } from './pages/CommunicationsPreferencesPage';
import { WorkStylePreferencesPage } from './pages/WorkStylePreferencesPage';
import { ProfileVisibilityPreferencesPage } from './pages/ProfileVisibilityPreferencesPage';
import { ProgramsPreferencesPage } from './pages/ProgramsPreferencesPage';
import { HelpCenterChangesPage } from './pages/HelpCenterChangesPage';
import { ExperienceEditPage } from './pages/ExperienceEditPage';
import { RedirectToEnvoyBios } from './components/RedirectToEnvoyBios/RedirectToEnvoyBios';
import { RedirectToOnboarding } from './components/RedirectToOnboarding/RedirectToOnboarding';
import { RedirectToWorkdayTimesheets } from './components/RedirectToWorkdayTimesheets/RedirectToWorkdayTimesheets';
import { TalentProfileClientViewPage } from './pages/TalentProfileClientViewPage';
import { TalentEngagementsPage } from './pages/TalentEngagementsPage';
import { TalentEngagementsHistoryPage } from './pages/TalentEngagementsHistoryPage';
import { EngagementsViewContent } from './components/EngagementsView/EngagementsViewContent';
import { EngagementsHistoryViewContent } from './components/EngagementsHistoryView/EngagementsHistoryViewContent';
import { EngagementsHistorySearchRequestFilterTabOptions } from './schema/engagements-history-search-request-schema';
import { EngagementsHistoryViewRedirect } from './components/EngagementsHistoryView/EngagementsHistoryViewRedirect';
import { AuthenticatedHeaderMenu } from './layouts/AuthenticatedHeaderMenu/AuthenticatedHeaderMenu';
import { EnvUtil } from './utils/env-util';

const AppHeaderBar = () => {
  const { candidateId: urlCandidateId } = useParams();
  const logoUrl =
    urlCandidateId && isUuid.anyNonNil(urlCandidateId)
      ? `/talent/${urlCandidateId}/profile`
      : '/';

  return (
    <HeaderBar name="TOPNAV" logoUrl={logoUrl}>
      <AuthenticatedHeaderMenu />
    </HeaderBar>
  );
};

export const AppRouter = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Page
            Header={<AppHeaderBar />}
            Footer={<Footer cookieProId={EnvUtil.cookieProId} />}
          >
            <Suspense>
              <Outlet />
            </Suspense>
          </Page>
        }
      >
        <Route
          path="talent/timesheets"
          element={
            <AuthRoute roles={[UserConst.Roles.LPUser]}>
              <RedirectToWorkdayTimesheets />
            </AuthRoute>
          }
        />
        <Route path="talent/:candidateId?">
          <Route
            path="profile"
            element={
              <AuthRoute
                roles={[
                  UserConst.Roles.LPUser,
                  UserConst.Roles.EnvoyAdmin,
                  UserConst.Roles.EnvoyUser,
                ]}
              >
                <Outlet />
              </AuthRoute>
            }
          >
            <Route
              path=""
              element={
                <TalentProfilePage key="profile" name="TALENT_PROFILE_PAGE" />
              }
            />
            <Route
              path="interview-availability"
              element={
                <TalentProfilePage
                  key="profile-interview-availability"
                  name="TALENT_PROFILE_PAGE"
                />
              }
            />
            <Route
              path="client-view"
              element={
                <TalentProfileClientViewPage name="TALENT_PROFILE_CLIENT_VIEW_PAGE" />
              }
            />
          </Route>
          <Route path="engagements">
            <Route
              path=""
              element={
                <AuthRoute
                  roles={[
                    UserConst.Roles.LPUser,
                    UserConst.Roles.EnvoyAdmin,
                    UserConst.Roles.EnvoyUser,
                  ]}
                >
                  <Suspense>
                    <TalentEngagementsPage name="TALENT_ENGAGEMENTS_PAGE" />
                  </Suspense>
                </AuthRoute>
              }
            >
              <Route
                path=":opportunityId?"
                element={
                  <Suspense fallback={<Loader type="bars" />}>
                    <EngagementsViewContent />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="history"
              element={
                <AuthRoute
                  roles={[
                    UserConst.Roles.LPUser,
                    UserConst.Roles.EnvoyAdmin,
                    UserConst.Roles.EnvoyUser,
                  ]}
                >
                  <TalentEngagementsHistoryPage name="TALENT_ENGAGEMENTS_HISTORY_PAGE" />
                </AuthRoute>
              }
            >
              {Object.entries(
                EngagementsHistorySearchRequestFilterTabOptions
              ).map(([slug, viewType]) => (
                <Route
                  key={slug}
                  path={slug}
                  element={
                    <Suspense fallback={<Loader type="bars" />}>
                      <EngagementsHistoryViewContent
                        key={`history-${slug}`}
                        viewType={viewType}
                      />
                    </Suspense>
                  }
                >
                  <Route path="page">
                    <Route
                      path=":pageNumber"
                      element={
                        <Suspense fallback={<Loader type="bars" />}>
                          <EngagementsHistoryViewContent
                            key={`history-${slug}-page`}
                            viewType={viewType}
                          />
                        </Suspense>
                      }
                    />
                    <Route path="" element={<Navigate to="../" replace />} />
                  </Route>
                </Route>
              ))}
              <Route path="*" element={<Navigate to=".." replace />} />
              <Route path="" element={<EngagementsHistoryViewRedirect />} />
            </Route>
            <Route
              path="opportunity-feed"
              element={
                <AuthRoute
                  roles={[
                    UserConst.Roles.LPUser,
                    UserConst.Roles.EnvoyAdmin,
                    UserConst.Roles.EnvoyUser,
                  ]}
                >
                  <Outlet />
                </AuthRoute>
              }
            >
              <Route path="" element={<Navigate to="all" />} />
              <Route
                path="all"
                element={
                  <WorkFeedPage key="work-feed-all" name="WORK_FEED_PAGE" />
                }
              >
                <Route path="page">
                  <Route path="" element={<Navigate to="../" replace />} />
                  <Route
                    path=":pageNumber"
                    element={
                      <WorkFeedPage
                        key="work-feed-all-page"
                        name="WORK_FEED_PAGE"
                      />
                    }
                  />
                </Route>
              </Route>
              <Route
                path="saved"
                element={
                  <WorkFeedPage key="work-feed-saved" name="WORK_FEED_PAGE" />
                }
              >
                <Route path="page">
                  <Route path="" element={<Navigate to="../" replace />} />
                  <Route
                    path=":pageNumber"
                    element={
                      <WorkFeedPage
                        key="work-feed-saved-page"
                        name="WORK_FEED_PAGE"
                      />
                    }
                  />
                </Route>
              </Route>
              <Route
                path="403"
                element={
                  <WorkFeedFailWhalePage name="WORK_FEED_FAIL_WHALE_PAGE" />
                }
              />
              <Route
                path="actionNeeded"
                element={
                  <WorkFeedActionNeededPage name="WORK_FEED_ACTION_NEEDED_PAGE" />
                }
              />
              <Route
                path=":opportunityId"
                element={
                  <SingleWorkFeedItemPage name="SINGLE_WORK_FEED_ITEM_PAGE" />
                }
              />
            </Route>
          </Route>
          <Route path="experiences">
            <Route
              path="new"
              element={
                <AuthRoute
                  roles={[
                    UserConst.Roles.LPUser,
                    UserConst.Roles.EnvoyAdmin,
                    UserConst.Roles.EnvoyUser,
                  ]}
                >
                  <ExperienceEditPage
                    key="experience-new"
                    name="EXPERIENCE_EDIT_PAGE"
                  />
                </AuthRoute>
              }
            />
            <Route
              path=":experienceId"
              element={
                <AuthRoute
                  roles={[
                    UserConst.Roles.LPUser,
                    UserConst.Roles.EnvoyAdmin,
                    UserConst.Roles.EnvoyUser,
                  ]}
                >
                  <ExperienceEditPage
                    key="experience-edit"
                    name="EXPERIENCE_EDIT_PAGE"
                  />
                </AuthRoute>
              }
            />
          </Route>
          <Route path="" element={<Navigate to="profile" />} />
          <Route path="opportunities">
            <Route
              path=""
              element={<Navigate to="../engagements/opportunity-feed" />}
            />
            <Route
              path="all"
              element={<Navigate to="../../engagements/opportunity-feed" />}
            />
          </Route>
          <Route
            path="work-feed-email"
            element={<Navigate to="../engagements/opportunity-feed" />}
          />
          <Route
            path="preferences"
            element={
              <AuthRoute
                roles={[
                  UserConst.Roles.LPUser,
                  UserConst.Roles.EnvoyAdmin,
                  UserConst.Roles.EnvoyUser,
                ]}
              >
                <Outlet />
              </AuthRoute>
            }
          >
            <Route path="" element={<Navigate to="communications" replace />} />
            <Route
              path="communications"
              element={
                <CommunicationsPreferencesPage name="COMMUNICATIONS_PREFERENCES_PAGE" />
              }
            />
            <Route
              path="work-style"
              element={
                <WorkStylePreferencesPage name="WORK_STYLE_PREFERENCES_PAGE" />
              }
            />
            <Route
              path="profile-visibility"
              element={
                <ProfileVisibilityPreferencesPage name="PROFILE_VISIBILITY_PREFERENCES_PAGE" />
              }
            />
            <Route
              path="programs"
              element={
                <ProgramsPreferencesPage name="PROGRAMS_PREFERENCES_PAGE" />
              }
            />
          </Route>
        </Route>
        <Route
          path="digital-submissions/:submissionId"
          element={<DigitalSubmissionsPage name="DIGITAL_SUBMISSION_PAGE" />}
        />
        <Route
          path="confirms/availability-preferences/:token"
          element={
            <AvailabilityPreferencesConfirmPage name="AVAILABILITY_PREFERENCES_CONFIRM_PAGE" />
          }
        />
        <Route path="bio/:candidateId" element={<RedirectToEnvoyBios />} />
        <Route
          path="anonymous-profile/:candidateId"
          element={<RedirectToEnvoyBios isAnonymous />}
        />
        <Route
          path="help-center-changes"
          element={<HelpCenterChangesPage name="HELP_CENTER_CHANGES_PAGE" />}
        />
        <Route
          path="401"
          element={<UnauthorizedPage name="UNAUTHORIZED_PAGE" />}
        />
        <Route
          path="404"
          element={
            <ErrorLayout
              errorMessage="Page not found."
              name="NOT_FOUND_ERROR_LAYOUT"
            />
          }
        />
        <Route path="" element={<Navigate to="talent/profile" />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
      <Route
        path="/onboarding/:opportunityId/:candidateId"
        element={
          <AuthRoute
            roles={[
              UserConst.Roles.EnvoySales,
              UserConst.Roles.EnvoyAdmin,
              UserConst.Roles.EnvoyUser,
              UserConst.Roles.LPUser,
            ]}
          >
            <RedirectToOnboarding />
          </AuthRoute>
        }
      />
    </Routes>
  );
};
